import { useHostedBudgetPayments99, useHostedWorksheetAttachments99 } from "./budgetPayments99Operations";
import { BudgetPayments99Service } from "./budgetPayments99Service";
import { GcCompensationService } from "./gcCompensationService";
import { useHostedGcCompensation } from "./gcCompensationOperations";
import {useHostedCompanyOperations96,useHostedCommitmentOperations96} from "./companyOperations96";
import {CompanySpendingService} from "./companySpendingService";
import {Accounting97Service} from "./accounting97Service";
import {BankReconciliation97Service} from "./bankReconciliation97Service";
import {useHostedAccounting97} from "./accounting97Operations";
import {useHostedCompanyFunding} from "./companyFundingOperations";
import type { DbExecutor } from "./executor";
import { StatusRepository } from "./repositories/statusRepository";
import { ProjectRepository } from "./repositories/projectRepository";
import { CategoryRepository } from "./repositories/categoryRepository";
import { AcquisitionRepository } from "./repositories/acquisitionRepository";
import { ProposedCostRepository } from "./repositories/proposedCostRepository";
import { ExpenseRepository } from "./repositories/expenseRepository";
import { FloorRepository } from "./repositories/floorRepository";
import { RoomRepository } from "./repositories/roomRepository";
import { FeatureRepository } from "./repositories/featureRepository";
import { AssemblyRepository } from "./repositories/assemblyRepository";
import { PurchaseRepository } from "./repositories/purchaseRepository";
import { AttachmentRepository } from "./repositories/attachmentRepository";
import { MileageRepository } from "./repositories/mileageRepository";
import { BackupService } from "./backupService";
import { CatalogRepository } from "./repositories/catalogRepository";
import { BomService } from "./bomService";
import { PlanRepository } from "./repositories/planRepository";
import { DesignScopeCoordinator } from "./designScopeCoordinator";
import { DesignSyncService } from "./designSyncService";
import { ProposedEditsService } from "./proposedEditsService";
import { ConflictService } from "./conflictService";
import { TaskRepository } from "./repositories/taskRepository";
import { CountertopService } from "./countertopService";
import { StairsRepository } from "./repositories/stairsRepository";
import { DesignChangeCoordinator } from "./designChangeCoordinator";
import { SettingsRepository } from "./repositories/settingsRepository";
import { ProcurementService } from "./procurementService";
import { ProviderSourcingService } from "./providerSourcingService";
import { ReconciliationService } from "./reconciliationService";
import { DocumentIntelligenceService } from "./documentIntelligenceService";
import { EstimateEvidenceService } from "./estimateEvidenceService";
import { ScopePackageRepository } from "./repositories/scopePackageRepository";
import { PackageService } from "./packageService";
import { BaselineService } from "./baselineService";
import { ProjectFinancialsService } from "./projectFinancialsService";
import { ReportService } from "./reportService";
import { OrganizationHistoryProvider } from "./evidence/organizationHistoryProvider";
import { ProductOfferProvider } from "./evidence/productOfferProvider";
import { TemplateAssumptionProvider } from "./evidence/templateAssumptionProvider";
import { ManualOverrideProvider } from "./evidence/manualOverrideProvider";
import { createUnavailableInterpreter, type DocumentInterpreter } from "../documents/interpreter";
import { LaborSettlementRepository } from "./repositories/laborSettlementRepository";
import { createUnavailableProviderExecutor, type ProviderExecutor } from "../sourcing/providerExecutor";
import { ProjectDeletionService } from "./projectDeletionService";
import { BidService } from "./bidService";
import { DesignRepository } from "./repositories/designRepository";
import { ObjectDefinitionRepository } from "./repositories/objectDefinitionRepository";
import { FinancingRepository } from "./repositories/financingRepository";
import { CapitalRepository } from "./repositories/capitalRepository";
import { CapitalPlanService } from "./capitalPlanService";
import { useHostedFinancialSnapshots } from "./financialSnapshots";
import { CommitmentService } from "./commitmentService";
import { BudgetApprovalService } from "./budgetApprovalService";
import { DrawRequestService } from "./drawRequestService";
import { Spending95Service } from "./spending95Service";
import { DailyBudget95Service } from "./dailyBudget95Service";
import { wireReceiptSettlement95 } from "./receiptSettlement95";
import { useHostedOwnerOperations95 } from "./ownerOperations95";
import { CategoryBudgetService } from "./categoryBudgetService";
import { FinishService } from "./finishService";
import { RenovationStandardService } from "./renovationStandardService";
import { RoomCopyService } from "./roomCopyService";
import { CompletionService } from "./completionService";
import { ActivationRepository } from "./repositories/activationRepository";
import { OpeningReplacementService } from "./openingReplacementService";
import { AnnotationRepository } from "./repositories/annotationRepository";
import { ExecutionForecastService } from "./executionForecastService";
import { CalibrationService } from "./calibrationService";
import { DealService } from "./dealService";
import type { FileStore } from "./fileStore";
import type { PlatformAdapter } from "../platform/platformAdapter";
import { createBrowserPlatformAdapter } from "../platform/browserPlatformAdapter";

export type DatabaseMode = "tauri" | "browser-preview" | "server";

export interface AppDatabase {
  accounting97:Accounting97Service;
  reconciliation97:BankReconciliation97Service;
  mode: DatabaseMode;
  db: DbExecutor;
  projects: ProjectRepository;
  statuses: StatusRepository;
  categories: CategoryRepository;
  acquisition: AcquisitionRepository;
  proposedCosts: ProposedCostRepository;
  expenses: ExpenseRepository;
  floors: FloorRepository;
  rooms: RoomRepository;
  features: FeatureRepository;
  assemblies: AssemblyRepository;
  purchases: PurchaseRepository;
  attachments: AttachmentRepository;
  mileage: MileageRepository;
  catalog: CatalogRepository;
  bom: BomService;
  /** Phase 9B: procurement fulfillment / sourcing workflows. */
  procurement: ProcurementService;
  /** Phase 9B.1: live provider-backed product sourcing. */
  sourcing: ProviderSourcingService;
  /** Phase 9C: cost-basis-aware reconciliation + provenance. */
  reconciliation: ReconciliationService;
  laborSettlements: LaborSettlementRepository;
  /** Phase 9D: receipt/invoice interpretation into the existing accounting. */
  documents: DocumentIntelligenceService;
  /** Phase 9E: evidence-backed estimating (history, offers, assumptions). */
  estimateEvidence: EstimateEvidenceService;
  /** Phase 11: baselines, change orders and the authorized budget. */
  baselines: BaselineService;
  /** Phase 12: the project financial dashboard, closeout and accounting view. */
  projectFinancials: ProjectFinancialsService;
  /** Phase 13: printable/exportable reports built from the same models. */
  reports: ReportService;
  /** Phase 10: the reusable scope-package library and its application. */
  scopePackages: ScopePackageRepository;
  packages: PackageService;
  /** Phase 7: building geometry persistence and the Design → Scope coordinator. */
  plans: PlanRepository;
  designScope: DesignScopeCoordinator;
  /** Existing → Proposed inheritance and its review list (spec §60H). */
  designSync: DesignSyncService;
  /** Spec §61B: the decisions a Proposed plan makes, and cancelling one. */
  proposedEdits: ProposedEditsService;
  /** Spec §61C: where two real things want the same space. */
  conflicts: ConflictService;
  /** Spec §62: the project's schedule — tasks, what waits on what. */
  tasks: TaskRepository;
  /** Spec §63: what a countertop is made of, and what it amounts to. */
  countertops: CountertopService;
  /** Stair dimensions for placed staircases (spec §60I). */
  stairs: StairsRepository;
  designChanges: DesignChangeCoordinator;
  settings: SettingsRepository;
  projectDeletion: ProjectDeletionService;
  /** Contractor bid packages and quotes (spec §68). */
  bids: BidService;
  /** Spec §73: financing facilities, draws, payment timing, rental assumptions. */
  financing: FinancingRepository;
  /** Spec §73: workspace-level capital, scenarios, plan settings/overrides. */
  capital: CapitalRepository;
  /** Spec §73: derives the financial-event timeline and portfolio cash plan. */
  capitalPlan: CapitalPlanService;
  commitments: CommitmentService;
  budgetApproval: BudgetApprovalService;
  drawRequests: DrawRequestService;
  budgetPayments99: BudgetPayments99Service;
  gcCompensation: GcCompensationService;
  spending95: Spending95Service;
  companySpending: CompanySpendingService;
  dailyBudget95: DailyBudget95Service;
  /** Spec §74: the derived category budget over the detailed estimate. */
  categoryBudget: CategoryBudgetService;
  /** Spec §82: whole-project flooring, wall paint and ceiling paint. */
  finishes: FinishService;
  /** Spec §90: how this company normally renovates, and one-click room copies. */
  standards: RenovationStandardService;
  roomCopy: RoomCopyService;
  /** Spec §91: the Completion Review, and what finished work teaches. */
  completion: CompletionService;
  /** Spec §86: where each project stands — derived, never stored. */
  activation: ActivationRepository;
  openingReplacements: OpeningReplacementService;
  /** Spec §83: drawing annotations — presentation, never geometry. */
  annotations: AnnotationRepository;
  calibration: CalibrationService;
  /** Spec §87: pre-acquisition deals (owner only; never on the shared surface). */
  deals: DealService;
  /** Spec §75: the chronological execution + money forecast. */
  executionForecast: ExecutionForecastService;
  /** Phase 7.1: design scenarios (Existing / Proposed alternatives). */
  designs: DesignRepository;
  /** Phase 8: reusable graphical object library. */
  objectDefs: ObjectDefinitionRepository;
  /** Null in browser-preview mode (no managed file storage). */
  backup: BackupService | null;
  /** True when managed file storage is available (desktop app). */
  filesAvailable: boolean;
  /** Platform capabilities (file picker, open external) behind one boundary. */
  platform: PlatformAdapter;
}

/** In-memory FileStore for browser-preview mode (non-persistent). */
function createMemoryFileStore(): FileStore {
  const store = new Map<string, Uint8Array>();
  return {
    async writeFile(p, bytes) {
      store.set(p, bytes);
    },
    async readFile(p) {
      const v = store.get(p);
      if (!v) throw new Error(`Missing file ${p}`);
      return v;
    },
    async exists(p) {
      return store.has(p);
    },
    async removeFile(p) {
      store.delete(p);
    },
    async absolutePath(p) {
      return `memory://${p}`;
    },
  };
}

/** True when running inside the Tauri shell (the real desktop application). */
export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** True when this page is served by the Berrier Reno test deployment. */
export async function isHostedDeployment(): Promise<boolean> {
  const { fetchSession } = await import("../session/session");
  return (await fetchSession()) !== null;
}

/**
 * Open the application database, enable foreign keys, run pending
 * migrations, and construct the repositories.
 *
 * In the packaged/desktop app this is always SQLite on disk via the Tauri
 * SQL plugin. In `vite dev` outside Tauri, a non-persistent in-memory
 * preview database is used so the UI can be exercised in a browser.
 */
/**
 * Ask the server to regenerate a project estimate in one request (§67B).
 * Hosted build only; the desktop runs the identical code locally.
 */
async function regenerateEstimateOnServer(projectId: string): Promise<void> {
  const response = await fetch("/api/project/regenerate-estimate", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectId }),
  });
  // The server just rewrote estimate lines this client may have read.
  const { invalidateHttpReads } = await import("./httpExecutor");
  invalidateHttpReads();
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error ?? "The estimate could not be brought up to date.");
  }
}

async function selectMaterialOnServer(projectId: string, componentIds: string[], productId: string, observationId: string | null): Promise<void> {
  const response = await fetch("/api/project/select-material", {
    method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projectId, componentIds, productId, observationId }),
  });
  const { invalidateHttpReads } = await import("./httpExecutor");
  invalidateHttpReads();
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error ?? "The material selection could not be saved.");
  }
}

function hostedSessionExpired():void { window.location.reload(); }
export async function openAppDatabase(): Promise<AppDatabase> {
  let mode: DatabaseMode;
  let db: DbExecutor;
  let files: FileStore;
  let platform: PlatformAdapter;
  let providerExecutor: ProviderExecutor;
  let interpreter: DocumentInterpreter;

  if (isTauri()) {
    const { createTauriExecutor } = await import("./tauriExecutor");
    const { createTauriFileStore } = await import("./tauriFileStore");
    const { createTauriPlatformAdapter } = await import("../platform/tauriPlatformAdapter");
    const { createTauriProviderExecutor } = await import("../sourcing/tauriProviderExecutor");
    db = await createTauriExecutor();
    files = createTauriFileStore();
    platform = createTauriPlatformAdapter();
    // The desktop shell holds the provider credential; this process never
    // sees it (spec §10 — the SaaS build swaps in a server endpoint).
    providerExecutor = createTauriProviderExecutor();
    const { createTauriDocumentInterpreter } = await import("../documents/tauriInterpreter");
    interpreter = createTauriDocumentInterpreter();
    mode = "tauri";
  } else if (await isHostedDeployment()) {
    // Phase 13A: the hosted test deployment. Same application, same
    // migrations, same repositories — the persistence boundary points at
    // the tester's own server-side workspace instead of the desktop shell.
    const { createHttpExecutor } = await import("./httpExecutor");
    const { createHttpFileStore } = await import("./httpFileStore");
    db = createHttpExecutor({
      // The session ended: back to the sign-in screen, no stale data.
      onUnauthorized: hostedSessionExpired,
    });
    files = createHttpFileStore();
    platform = createBrowserPlatformAdapter();
    // Phase 13B: the provider credential lives on the Berrier Reno server,
    // never in this bundle. Same interface as the desktop's Rust transport.
    const { createHttpProviderExecutor } = await import("../sourcing/httpProviderExecutor");
    providerExecutor = createHttpProviderExecutor();
    // Receipt reading: the SERVER holds the Azure credential and makes the
    // call, the same boundary the desktop implements in Rust. If no
    // credential is configured there, this interpreter says so honestly.
    const { createHttpDocumentInterpreter } = await import("../documents/httpInterpreter");
    interpreter = createHttpDocumentInterpreter();
    mode = "server";
  } else if (import.meta.env.DEV) {
    const { createBrowserPreviewExecutor } = await import("./browserPreviewExecutor");
    db = await createBrowserPreviewExecutor();
    files = createMemoryFileStore();
    platform = createBrowserPlatformAdapter();
    providerExecutor = createUnavailableProviderExecutor(
      "Live Home Depot search runs in the desktop application, which holds the provider credential.",
    );
    interpreter = createUnavailableInterpreter(
      "Receipt reading runs in the desktop application, which holds the provider credential.",
    );
    mode = "browser-preview";
  } else {
    throw new Error(
      "Berrier Reno must run inside its desktop application shell.",
    );
  }

  await db.execute("PRAGMA foreign_keys = ON");
  // MIGRATIONS BELONG TO WHOEVER OPENS THE FILE (spec §88B). On the desktop and
  // in the browser preview that is this code. On the hosted build it is the
  // server: every workspace access goes through WorkspaceRegistry.get →
  // Workspace.open, which runs the SAME runMigrations before the first
  // statement is answered, and the client and server ship from one commit.
  // Running it here too only proved that — at the cost of downloading the
  // 196 kB catalogue on every hosted open. Loaded on demand otherwise (§88A).
  if (mode !== "server") {
    const { runMigrations } = await import("./migrations");
    await runMigrations(db);
  }

  const attachments = new AttachmentRepository(db, files);
  const assemblies = new AssemblyRepository(db);
  const catalog = new CatalogRepository(db);
  const bids = new BidService(db);
  const bom = new BomService(db, assemblies, catalog);
  assemblies.setProductPricingHook(bom);
  if (mode === "server") {
    // THE SAME WORK, ON THE OTHER SIDE OF THE WIRE (spec §67B). Hundreds of
    // statements is nothing against a local file and over two minutes when
    // each one is an HTTP round trip.
    assemblies.setBulkRegenerationHook(regenerateEstimateOnServer);
  }
  const rooms = new RoomRepository(db);
  const features = new FeatureRepository(db);
  const plans = new PlanRepository(db);
  const objectDefs = new ObjectDefinitionRepository(db);
  const designScope = new DesignScopeCoordinator(db, plans, rooms, features, assemblies, objectDefs);
  const designChanges = new DesignChangeCoordinator(db, plans, features, assemblies, designScope);
  designScope.setChangeCoordinator(designChanges);
  // Spec §60H: an Existing edit flows forward into every plan that has no
  // opinion about it; the rest becomes something to look at.
  const stairs = new StairsRepository(db);
  const floorRepo = new FloorRepository(db);
  const designSync = new DesignSyncService(db);
  const proposedEdits = new ProposedEditsService(db);
  const conflicts = new ConflictService(db);
  const tasks = new TaskRepository(db);
  const countertops = new CountertopService(db);
  designScope.setSyncService(designSync);
  const settings = new SettingsRepository(db);
  const projectDeletion = new ProjectDeletionService(db, mode === "browser-preview" ? null : files);
  const designs = new DesignRepository(db);
  const purchases = new PurchaseRepository(db);
  const procurement = new ProcurementService(db, bom, catalog, purchases, assemblies);
  if (mode === "server") procurement.setSelectionHook(selectMaterialOnServer);
  const sourcing = new ProviderSourcingService(db, providerExecutor, catalog, assemblies, bom);
  const proposedCosts = new ProposedCostRepository(db);
  const expenses = new ExpenseRepository(db);
  const mileage = new MileageRepository(db);
  const laborSettlements = new LaborSettlementRepository(db);
  const documents = new DocumentIntelligenceService(
    db, interpreter, attachments, catalog, purchases, laborSettlements,
  );
  const estimateEvidence = new EstimateEvidenceService(db, assemblies, [
    new ManualOverrideProvider(db),
    new OrganizationHistoryProvider(db, assemblies, expenses, purchases, mileage),
    new ProductOfferProvider(db),
    new TemplateAssumptionProvider(db),
  ]);
  const scopePackages = new ScopePackageRepository(db);
  const packageService = new PackageService(
    db, scopePackages, features, assemblies, rooms, catalog, designScope,
  );
  const baselines = new BaselineService(
    db, proposedCosts, features, assemblies, rooms, designs, settings,
  );
  const reconciliation = new ReconciliationService(
    db, proposedCosts, expenses, purchases, mileage, features, laborSettlements, procurement, attachments,
  );
  const projects = new ProjectRepository(db);
  const categories = new CategoryRepository(db);
  const acquisition = new AcquisitionRepository(db);
  const projectFinancials = new ProjectFinancialsService(
    db, projects, acquisition, categories, mileage, reconciliation, baselines, procurement,
  );
  const statuses = new StatusRepository(db);
  const financing = new FinancingRepository(db);
  const capital = new CapitalRepository(db);
  const categoryBudget = new CategoryBudgetService(db, bids, proposedCosts, designs, categories, reconciliation);
  laborSettlements.setMixedCoverageResolver(p=>categoryBudget.mixedSettlementContext(p));
  const finishes = new FinishService(db, features, assemblies);
  const openingReplacements = new OpeningReplacementService(db, features, assemblies, plans);
  openingReplacements.setScopeCoordinator(designScope);
  designScope.setOpeningReplacements(openingReplacements);
  designScope.setAutomaticFinishReconciler((projectId, designId) => finishes.reconcileAutomatic(projectId, designId));
  finishes.setAutoRemovableResolver((featureId) => designScope.isAutoRemovable(featureId));
  const annotations = new AnnotationRepository(db);
  const calibration = new CalibrationService(db, proposedCosts, categoryBudget, reconciliation);
  const standards = new RenovationStandardService(db, finishes, calibration);
  const roomCopy = new RoomCopyService(db, rooms, features, assemblies, designScope);
  /* Spec §91: closing out a renovation, and what the company learns from it. */
  const completion = new CompletionService(db, proposedCosts, designs, categories, reconciliation, bids, calibration);
  /* Spec §90: where the Phase 8.1 rule cannot decide between equally valid
   * assemblies, the project's own renovation standard already has. */
  designScope.setStandardSpecResolver((projectId, definitionId) => standards.specForProject(projectId, definitionId));
  const deals = new DealService(db, projects, acquisition, financing, calibration, projectFinancials);
  const commitments = new CommitmentService(db, id => reconciliation.eventsForProject(id));
  const budgetApproval = new BudgetApprovalService(db, baselines, categoryBudget, bids);
  const drawRequests = new DrawRequestService(db, id=>reconciliation.eventsForProject(id), financing);
  const companySpending = new CompanySpendingService(db,purchases);
  const accounting97=new Accounting97Service(db),reconciliation97=new BankReconciliation97Service(db);
  accounting97.setPostDateGuard((ids,date)=>reconciliation97.assertPostingAllowed(ids,date));
  accounting97.setReversalGuard((id,date)=>reconciliation97.assertReversalAllowed(id,date));
  commitments.setCompanyPaymentSource(()=>companySpending.businessPayments());
  commitments.setCompanyInvoiceSource(()=>companySpending.invoicePayments());
  const spending95 = new Spending95Service(db,expenses,purchases,commitments,financing,attachments,laborSettlements);
  const budgetPayments99 = new BudgetPayments99Service(db,drawRequests,categoryBudget,bids,spending95,p=>reconciliation.eventsForProject(p),commitments);
  const gcCompensation = new GcCompensationService(db,budgetPayments99,drawRequests,financing);
  budgetPayments99.setProjectDeletion((p,confirm)=>projectDeletion.deleteProject(p,confirm));
  budgetPayments99.setCompletedWorkReader(async p=>(await completion.review(p)).trades.flatMap(t=>t.items).filter(i=>i.state==='complete').map(i=>({key:i.key,label:i.label,featureIds:i.featureIds,proposedCents:i.proposedCents})));
  const dailyBudget95 = new DailyBudget95Service(db,projectFinancials,budgetApproval,commitments,reconciliation);
  documents.setBusinessImportHook((purchaseId,input)=>companySpending.annotateImported(purchaseId,input));
  wireReceiptSettlement95(documents,purchases,commitments);
  if(mode==="server"){useHostedCompanyOperations96(companySpending,db,hostedSessionExpired);useHostedCommitmentOperations96(commitments,db,hostedSessionExpired);useHostedAccounting97(accounting97,reconciliation97,db,hostedSessionExpired);}
  if(mode==="server"){useHostedBudgetPayments99(budgetPayments99,db,hostedSessionExpired);useHostedWorksheetAttachments99(attachments,db,hostedSessionExpired);projectDeletion.deleteProject=(p,confirm)=>budgetPayments99.run("deleteProject",p,{confirmJobNumber:confirm});}
  if(mode==="server")useHostedOwnerOperations95({documents,budgetApproval,drawRequests,spending95,dailyBudget95},db,hostedSessionExpired);
  const capitalPlan = new CapitalPlanService(
    db, proposedCosts, acquisition, tasks, financing, capital, reconciliation, projectFinancials, bids, designs,
    projects, categoryBudget,
  );
  // Spec §73.1H: projected loan interest and fees reach the project's cost
  // breakdown and profit forecast through the ONE financing engine.
  capitalPlan.setCommitments(commitments);
  capitalPlan.setFundingEvidence(reconciliation97,accounting97,drawRequests,budgetApproval);
  capitalPlan.setGcCompensation(gcCompensation);
  budgetPayments99.setCashReader(async p=>(await capitalPlan.projectPlan(p,new Date().toISOString().slice(0,10))).events);
  dailyBudget95.setUpcomingReader((p,from,to,asof)=>capitalPlan.calendar(p,from,to,asof));
  projectFinancials.setFinancingProjector((pid) => capitalPlan.projectedFutureFinancing(pid));
  // Spec §74S: selected quotes and authorized category budgets adjust the
  // projected renovation cost through the ONE category engine.
  projectFinancials.setRenovationBasisDelta((pid, projection) => categoryBudget.basisDelta(pid, projection));
  const executionForecast = new ExecutionForecastService(
    db, tasks, financing, capital, capitalPlan, categoryBudget,
  );
  // Spec §75R: the Capital Plan sees the same planned draws the
  // single-project forecast derives — one engine, one answer.
  capitalPlan.setForecastBridge((pid) => executionForecast.forecastBridge(pid));
  if (mode === "server") useHostedFinancialSnapshots(capitalPlan, db, {onUnauthorized:hostedSessionExpired});
  if (mode === "server") useHostedCompanyFunding(capitalPlan,capital,db,hostedSessionExpired);
  if (mode === "server") useHostedGcCompensation(gcCompensation,db,hostedSessionExpired);
  const reports = new ReportService(
    db, projects, statuses, categories, acquisition, features, rooms, mileage, purchases, attachments, designs,
    baselines, procurement, reconciliation, projectFinancials,
    // Spec §60M: a printable floor plan needs the plan itself.
    floorRepo, plans, objectDefs, stairs, annotations,
    bids, categoryBudget, executionForecast, calibration, completion, capitalPlan, drawRequests,
  );

  return {
    mode,
    db,
    projects,
    statuses,
    categories,
    acquisition,
    proposedCosts,
    expenses,
    floors: floorRepo,
    rooms,
    features,
    assemblies,
    purchases,
    attachments,
    mileage,
    catalog,
    bom,
    procurement,
    sourcing,
    reconciliation,
    laborSettlements,
    documents,
    estimateEvidence,
    baselines,
    projectFinancials,
    reports,
    scopePackages,
    packages: packageService,
    standards,
    roomCopy,
    completion,
    plans,
    designScope,
    proposedEdits,
    conflicts,
    tasks,
    countertops,
    designSync,
    stairs,
    designChanges,
    settings,
    projectDeletion,
    bids,
    financing,
    capital,
    capitalPlan,
    commitments,
    budgetApproval,
    drawRequests,
    budgetPayments99,
    gcCompensation,
    spending95,
    companySpending,
    accounting97,reconciliation97,
    dailyBudget95,
    categoryBudget,
    finishes,
    activation: new ActivationRepository(db),
    openingReplacements,
    annotations,
    executionForecast,
    calibration,
    deals,
    designs,
    objectDefs,
    backup: mode === "tauri" ? new BackupService(db, files, attachments) : null,
    filesAvailable: mode !== "browser-preview",
    platform,
  };
}
